import { useEffect, useRef, useState } from "react";
import { CustomTabs, CustomToast, GnbFooter } from "../../shared";
import { useGetNickName } from "./model";
import { NickNameBottomSheet } from "./ui/nickNameBottomSheet";
import RecommendFeed from "./ui/recommendFeed";
import CreateFeedForm from "./ui/CreateFeedForm";
import { isLogin } from "../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { setUrl } from "../../service";
import { useMyContext } from "../../entities/context";
import { ReactComponent as Check } from "../../assets/images/icon/check.svg";
import { ReactComponent as Edit } from "../../assets/images/icon/edit.svg";
import MyFeed from "./ui/myFeed";
import { isIOS } from "react-device-detect";
import CommunityPolicyBanner from "./ui/policyBanner";
import CommunitySkeleton from "../../components/skeleton/community";

const Community = () => {
  const [initLoading, setInitLoading] = useState(true);
  const { data: nickName, isPending } = useGetNickName(isLogin());
  const [isShowNickNameBottomSheet, setIsShowNickNameBottomSheet] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [isShowToast, setIsShowToast] = useState<boolean>(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isToast = queryParams.get("isToast");
  const { topHeight, communityTabKey, setCommunityTabKey, isGnbFooterHidden, setIsGnbFooterHidden } = useMyContext();
  const [childrenToast, setChildrenToast] = useState<boolean>(false);
  const [toastTextKey, setToastTextKey] = useState<'delete' | 'claim'>('delete');
  const [isScrollCheck, setIsScrollCheck] = useState<boolean>(false);
  const [scrollOver, setScrollOver] = useState<number>(0)

  const getToastText = (): React.ReactNode => {
    if (toastTextKey === 'delete') {
      return <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        <Check width={16} height={16} stroke={"var(--secondarys100)"} />
        <div className="body/b2 base/white">삭제했습니다</div>
      </div>
    } else if (toastTextKey === 'claim') {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <Check width={16} height={16} stroke={'var(--secondarys100)'} />
        <div className="body/b2 base/white">
          신고가 접수되었습니다. 감사합니다.
        </div>
      </div>
    }
    return <></>;
  };

  useEffect(() => {
    if (!isLogin()) {
      setUrl(window.location.pathname);
      navigate("/login");
    }
  }, [isLogin()]);

  useEffect(() => {
    if (isPending) return;
    if (nickName === undefined) return;
    const isNickNameEmpty = !nickName;
    setIsShowNickNameBottomSheet(isNickNameEmpty);
  }, [isPending]);

  useEffect(() => {
    if (isToast === 'isDeleteToast' || isToast === 'isClaimToast') {
      const toastKey = isToast === 'isDeleteToast' ? 'delete' : 'claim';
      setToastTextKey(toastKey);
      setIsShowToast(true);
      queryParams.delete("isToast");
      const newUrl = `${location.pathname}${queryParams.size > 0 ? `?${queryParams.toString()}` : ''}`;
      navigate(newUrl);
    }
  }, [isToast]);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const onScroll = () => {
      const currentScrollY = window.scrollY;
      if (Math.abs(currentScrollY - lastScrollY) > 5) {
        const scrollDir = currentScrollY > lastScrollY ? "down" : "up";
        lastScrollY = currentScrollY;
        const isScrolledEnough = currentScrollY >= Number(topHeight) + 36 + 40;
        const isScrollCheck = currentScrollY >= 59;
        if (scrollDir === 'down') {
          setIsGnbFooterHidden(isScrolledEnough)
          setIsScrollCheck(isScrollCheck)
        } else {
          setIsGnbFooterHidden(isScrolledEnough)
          setIsScrollCheck(isScrollCheck)
        }

        if (isScrollCheck) {
          setScrollOver(currentScrollY - 59)
        } else {
          setScrollOver(0)
        }
      }

    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    return () => {
      setIsGnbFooterHidden(false)
    }
  }, [])

  return (
    <>
      {initLoading && <CommunitySkeleton />}
      <div>
        <div
          className="sub-title/st5 secondary/s800"
          style={{
            width: "calc(100% - 40px)",
            paddingInline: "20px",
            paddingBottom: "16px",
            paddingTop: `${Number(topHeight) + 16}px`,
            backgroundColor: "var(--basewhite)",
          }}
        >
          커뮤니티
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <CustomTabs
          styles={{
            container: {
              position: "sticky",
              top: 0,
              transition: "padding-top 0.2s ease-out",
              paddingTop: isScrollCheck ? scrollOver <= Number(topHeight) ? scrollOver : Number(topHeight) : 0,
              marginBottom: "24px",
              zIndex: 1,
              backgroundColor: "var(--basewhite)",
            },
            header: { height: "auto", paddingBlock: "8px" },
            defaultHeader: { borderBottom: "2px solid var(--grayg100)" },
          }}
          defaultkey={communityTabKey}
          onClick={(key) => setCommunityTabKey(key)}
          items={[
            {
              key: 0,
              label: "피드",
              children: (
                <>
                  <CommunityPolicyBanner />
                  {nickName && (
                    <div style={{ paddingInline: 20, marginBottom: 20 }}>
                      <CreateFeedForm nickName={nickName} />
                    </div>
                  )}
                  <RecommendFeed onToastChange={setChildrenToast} setInitLoading={setInitLoading} />
                </>
              ),
            },
            {
              key: 1,
              label: "내가 쓴 글",
              children: <MyFeed nickName={nickName} onToastChange={setChildrenToast} setInitLoading={setInitLoading} />,
            },
          ]}
        />
      </div>
      <Edit
        fill="var(--secondarys100)"
        onClick={() => navigate("/create/feed")}
        width={24}
        height={24}
        style={{
          position: "fixed",
          bottom:
            isGnbFooterHidden && !childrenToast
              ? 20 + (isIOS ? 34 : 0)
              : "-48px",
          transition: "bottom 0.3s ease",
          right: "20px",
          padding: "12px",
          backgroundColor: "var(--baseblack)",
          borderRadius: "100%",
        }}
      />

      <GnbFooter
        activeButton="community"
        styles={{
          container: {
            transition: "bottom 0.3s ease",
            bottom:
              isGnbFooterHidden
                ? isIOS
                  ? "-106.3px"
                  : "-72.3px"
                : 0,
          },
          hardDiv: {
            height: isGnbFooterHidden
              ? 40
              : isIOS
                ? "106.3px"
                : "72.3px",
          }
        }}
      />

      <NickNameBottomSheet
        isNickNameEmpty={!nickName}
        isOpen={isShowNickNameBottomSheet}
        setIsOpen={setIsShowNickNameBottomSheet}
      />

      {isShowToast && (
        <CustomToast
          text={getToastText()}
          setToast={setIsShowToast}
          style={{
            backgroundColor: "var(--dimd300)",
          }}
        />
      )}
    </>
  );
};

export { Community };
